import React, { useState, useEffect } from 'react';
import PropTypes from "prop-types";

import { apiConfig } from 'config/apiConfig'

import useFetchWithMsal from 'hooks/useFetchWithMsal';

import { FormControlLabel, Grid, Radio, RadioGroup, Tooltip, tooltipClasses } from '@mui/material';
import { Formik, Form, ErrorMessage } from 'formik';
import FormField from 'components/Elements/Forms/FormField';
import SoftAvatar from 'components/SoftAvatar';
import FormSwitch from 'components/Elements/Forms/FormSwitch';
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

import { styled } from '@mui/material/styles';


import validations from "./schemas/validations";
import form from "./schemas/form";
import initialValues from "./schemas/initialValues";

import { ProgressIndicator, useProgressIndicator } from 'components/Elements/ProgressIndicator';

import { useCardroomContext } from 'features';


const CustomWidthTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: 500,
    },
});


const FeeScheduleEditorForm = ({ cardroom, club, book, schedule, formData, dense }) => {

    const { formField, values, errors, touched, setFieldValue, setFieldError, setFieldTouched, validateForm, resetForm } = formData;
    const { scheduleId, name, description, feeRate, feeThreshold, feeScheduleScheme, aggregate, chipOffsetRate, taxRebateRate, makeupThreshold, excludeRevenueFromTax } = formField;
    const {
        scheduleId: scheduleIdV,
        name: nameV,
        description: descriptionV,
        feeRate: feeRateV,
        feeThreshold: feeThresholdV,
        makeupThreshold: makeupThresholdV,
        feeScheduleScheme: feeScheduleSchemeV,
        aggregate: aggregateV,
        chipOffsetRate: chipOffsetRateV,
        taxRebateRate: taxRebateRateV,
        excludeRevenueFromTax: excludeRevenueFromTaxV
    } = values;

    const [formDataInitialized, setFormDataInitialized] = useState(false);


    const [context, actions, features] = useCardroomContext(cardroom);

    function canEditSchedule() {
        if (!context) return false;


        return context.isAllowed(actions.cardroom.fees.agent.edit);
    }

    function getFeeShemeDisplayText(v) {
        if (v === 0) return "none";
        if (v === 1) return "flat";
        if (v === 2) return "progressive";
        return "none";
    }

    function getFeeSchemeDescription(v) {
        if (v === 0) return "no fees are imputed";
        if (v === 1) return "Fees are imputed as percentage of total earnings";
        if (v === 2) return "Fees are imputed as percentage of earnings above commission threshold.";

        return "unknown";
    }

    function onFeeSchedChanged(e, newValue) {
        let scheme = parseInt(newValue);
        if (isNaN(scheme)) return;
        setFieldValue(feeScheduleScheme.name, scheme);
    }

    useEffect(() => {
        if (!formDataInitialized) {

            setFieldValue(feeScheduleScheme.name, schedule && schedule.id ? schedule.scheme : 0);
            setFieldValue(feeRate.name, schedule && schedule.id ? schedule.rate * 100 : 0);
            setFieldValue(name.name, schedule && schedule.id ? schedule.name : "");
            setFieldValue(description.name, schedule && schedule.id ? schedule.description : "");
            setFieldValue(scheduleId.name, schedule ? schedule.id : "");
            setFieldValue(feeThreshold.name, schedule && schedule.id ? schedule.threshold : 0);

            if (cardroom.type !== 6) {
                setFieldValue(chipOffsetRate.name, schedule && schedule.id ? schedule.chipoffsetRate * 100 : 0);
                setFieldValue(taxRebateRate.name, schedule && schedule.id ? schedule.taxRebateRate * 100 : 0);

                setFieldValue(aggregate.name, schedule && schedule.id ? schedule.aggregate : false);

                setFieldValue(excludeRevenueFromTax.name, schedule && !isNaN(schedule.excludeFromTax) ? schedule.excludeFromTax : 32);

            }
            else {
                setFieldValue(aggregate.name, false);
                setFieldValue(makeupThreshold.name, schedule && schedule.id ? schedule.makeupThreshold : 0);
                setFieldValue(excludeRevenueFromTax.name, 0);
            }


            setFormDataInitialized(true);
        }
    }, [formDataInitialized]);

    useEffect(() => {
        if (schedule && formDataInitialized) {
            setFormDataInitialized(false);
        }
    }, [schedule])

    return <SoftBox>
        <SoftBox mt={2}>
            <Grid container spacing={1}>
                {dense === true ? null :
                    <Grid item xs={12} sm={4} container justifyContent="center">
                        <SoftBox position="relative" height="max-content" mx="auto">
                            <SoftAvatar src={null} size="xxl" variant="rounded" bgColor="dark" />
                        </SoftBox>
                    </Grid>}
                <Grid item xs={12} sm={dense === true ? 12 : 8}>
                    <SoftBox mb={2} textAlign="left">

                        <Grid container xs={12}>
                            <Grid item xs={4} >
                                <SoftBox mb={1} mt={1.25} ml={0.5} lineHeight={0} display="inline-block" >
                                    <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                        textTransform="capitalize">
                                        {name.label}
                                    </SoftTypography>
                                </SoftBox>
                            </Grid>
                            <Grid item xs={8}>
                                <FormField disabled={!canEditSchedule()}
                                    type={name.type}
                                    name={name.name}
                                    value={nameV}
                                    placeholder={name.placeholder}
                                    error={errors.name && touched.name}
                                    success={nameV && nameV.length > 0 && !errors.name}
                                />
                            </Grid>
                        </Grid>

                        <Grid container xs={12}>
                            <Grid item xs={4} >
                                <SoftBox mb={1} mt={1.25} ml={0.5} lineHeight={0} display="inline-block" >
                                    <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                        textTransform="capitalize">
                                        {description.label}
                                    </SoftTypography>
                                </SoftBox>
                            </Grid>
                            <Grid item xs={8}>
                                <FormField disabled={!canEditSchedule()}
                                    type={description.type}
                                    name={description.name}
                                    value={descriptionV}
                                    placeholder={description.placeholder}
                                    error={errors.description && touched.description}
                                    success={descriptionV && descriptionV.length > 0 && !errors.description}
                                />
                            </Grid>
                        </Grid>

                        {(cardroom.type !== 6) ? <>
                            <Grid container xs={12}>
                                <Grid item xs={4} >
                                    <SoftBox mb={1} mt={1.25} ml={0.5} lineHeight={0} display="inline-block" >
                                        <SoftTypography
                                            component="label"
                                            variant="caption"
                                            fontWeight="bold"
                                            textTransform="capitalize">
                                            {chipOffsetRate.label}
                                        </SoftTypography>
                                    </SoftBox>
                                </Grid>
                                <Grid item xs={8}>
                                    <FormField disabled={!canEditSchedule()}
                                        type={chipOffsetRate.type}
                                        name={chipOffsetRate.name}
                                        value={chipOffsetRateV}
                                        placeholder={chipOffsetRate.placeholder}
                                        error={errors.chipOffsetRate && touched.chipOffsetRate}
                                        success={chipOffsetRateV && chipOffsetRateV.toString().length > 0 && !errors.chipOffsetRate}
                                    />
                                </Grid>
                            </Grid>


                            <Grid container xs={12}>
                                <Grid item xs={4} >
                                    <SoftBox mb={1} mt={1.25} ml={0.5} lineHeight={0} display="inline-block" >
                                        <SoftTypography
                                            component="label"
                                            variant="caption"
                                            fontWeight="bold"
                                            textTransform="capitalize">
                                            {taxRebateRate.label}
                                        </SoftTypography>
                                    </SoftBox>
                                </Grid>
                                <Grid item xs={8}>
                                    <FormField disabled={!canEditSchedule()}
                                        type={taxRebateRate.type}
                                        name={taxRebateRate.name}
                                        value={taxRebateRateV}
                                        placeholder={taxRebateRate.placeholder}
                                        error={errors.taxRebateRate && touched.taxRebateRate}
                                        success={taxRebateRateV && taxRebateRateV.toString().length > 0 && !errors.taxRebateRate}
                                    />
                                </Grid>
                            </Grid>
                        </> : null}

                        <Grid container xs={12}>
                            <Grid item xs={4} >
                                <SoftBox mt={1.25} ml={0.5} lineHeight={0} display="inline-block" >
                                    <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                        textTransform="capitalize"
                                    >
                                        {feeScheduleScheme.label}
                                    </SoftTypography>
                                </SoftBox>
                            </Grid>
                            <Grid item xs={8}>
                                <SoftTypography
                                    variant="caption"
                                    textTransform="capitalize">
                                    {canEditSchedule() ? <>
                                        <RadioGroup row={true} defaultValue={feeScheduleSchemeV} value={feeScheduleSchemeV} onChange={onFeeSchedChanged} style={{ marginLeft: "1.25em", marginTop: "3px" }}>
                                            <CustomWidthTooltip title={getFeeSchemeDescription(0)} placement="bottom">
                                                <FormControlLabel value="0" control={<Radio />} label={getFeeShemeDisplayText(0)} />
                                            </CustomWidthTooltip>
                                            <CustomWidthTooltip title={getFeeSchemeDescription(1)} placement="bottom">
                                                <FormControlLabel value="1" control={<Radio />} label={getFeeShemeDisplayText(1)} />
                                            </CustomWidthTooltip>
                                            <CustomWidthTooltip title={getFeeSchemeDescription(2)} placement="bottom">
                                                <FormControlLabel value="2" control={<Radio />} label={getFeeShemeDisplayText(2)} />
                                            </CustomWidthTooltip>
                                        </RadioGroup>
                                        <SoftTypography
                                            component="label"
                                            variant="caption"
                                            color="error">
                                            <ErrorMessage name={feeScheduleScheme.name} />
                                        </SoftTypography>
                                    </> : getFeeShemeDisplayText(feeScheduleSchemeV)}
                                </SoftTypography>
                            </Grid>
                        </Grid>


                        {feeScheduleSchemeV != 0 ? <>

                            {(cardroom.type !== 6) ?
                                <Grid container xs={12}>
                                    <Grid item xs={4} >
                                        <SoftBox mb={1} mt={1.25} ml={0.5} lineHeight={0} display="inline-block" >
                                            <SoftTypography
                                                component="label"
                                                variant="caption"
                                                fontWeight="bold"
                                                textTransform="capitalize"
                                            >
                                                {aggregate.label}
                                            </SoftTypography>
                                        </SoftBox>
                                    </Grid>
                                    <Grid item xs={8}>
                                        <FormSwitch mr="auto" readOnly={!canEditSchedule()}
                                            name={aggregate.name}
                                            value={aggregateV}
                                            {...formData} />

                                        <SoftTypography
                                            component="label"
                                            variant="caption"
                                            color="info" >
                                            {aggregateV ?
                                                "Commission will calculated based on all players' results."
                                                : "Comission will be calculated based on each individual player results."}

                                        </SoftTypography>
                                    </Grid>
                                </Grid> : null}

                            <Grid container xs={12}>
                                <Grid item xs={4} >
                                    <SoftBox mb={1} mt={1.25} ml={0.5} lineHeight={0} display="inline-block" >
                                        <SoftTypography
                                            component="label"
                                            variant="caption"
                                            fontWeight="bold"
                                            textTransform="capitalize"
                                        >
                                            {feeRate.label}
                                        </SoftTypography>
                                    </SoftBox>
                                </Grid>
                                <Grid item xs={8}>
                                    <FormField disabled={!canEditSchedule()}
                                        type={feeRate.type}
                                        name={feeRate.name}
                                        value={feeRateV}
                                        placeholder={feeRate.placeholder}
                                        error={errors.feeRate && touched.feeRate}
                                        success={feeRateV && feeRateV.toString().length > 0 && !errors.feeRate}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container xs={12}>
                                <Grid item xs={4} >
                                    <SoftBox mb={1} mt={1.25} ml={0.5} lineHeight={0} display="inline-block" >
                                        <SoftTypography
                                            component="label"
                                            variant="caption"
                                            fontWeight="bold"
                                            textTransform="capitalize"
                                        >
                                            {feeThreshold.label}
                                        </SoftTypography>
                                    </SoftBox>
                                </Grid>
                                <Grid item xs={8}>
                                    <FormField disabled={!canEditSchedule()}
                                        type={feeThreshold.type}
                                        name={feeThreshold.name}
                                        value={feeThresholdV}
                                        placeholder={feeThreshold.placeholder}
                                        error={errors.feeThreshold && touched.feeThreshold}
                                        success={feeThresholdV && feeThresholdV.toString().length > 0 && !errors.feeThreshold}
                                    />
                                </Grid>
                            </Grid>

                            {cardroom.type === 6 ? <Grid container xs={12}>
                                <Grid item xs={4} >
                                    <SoftBox mb={1} mt={1.25} ml={0.5} lineHeight={0} display="inline-block" >
                                        <SoftTypography
                                            component="label"
                                            variant="caption"
                                            fontWeight="bold"
                                            textTransform="capitalize"
                                        >
                                            {makeupThreshold.label}
                                        </SoftTypography>
                                    </SoftBox>
                                </Grid>
                                <Grid item xs={8}>
                                    <FormField disabled={!canEditSchedule()}
                                        type={makeupThreshold.type}
                                        name={makeupThreshold.name}
                                        value={makeupThresholdV}
                                        placeholder={makeupThreshold.placeholder}
                                        error={errors.makeupThreshold && touched.makeupThreshold}
                                        success={makeupThresholdV && makeupThresholdV.toString().length > 0 && !errors.makeupThreshold}
                                    />
                                </Grid>
                            </Grid> : null}
                        </>
                            : null}

                        {(cardroom.type !== 6) ? <Grid container xs={12}>
                            <Grid item xs={4} >
                                <SoftBox mb={1} mt={1.25} ml={0.5} lineHeight={0} display="inline-block" >
                                    <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                        textTransform="capitalize"
                                    >
                                        {excludeRevenueFromTax.label}
                                    </SoftTypography>
                                </SoftBox>
                            </Grid>
                            <Grid item xs={8}>
                                {formDataInitialized ?

                                    <Grid container xs={12}>
                                        <Grid item xs={3}>
                                            <SoftBox ml={0.5} mb={0.25}>
                                                <FormSwitch label="Cash" readOnly={!canEditSchedule()}
                                                    name={excludeRevenueFromTax.name}
                                                    value={excludeRevenueFromTaxV}
                                                    mask={1}
                                                    variant="bit"
                                                    {...formData} />
                                            </SoftBox>
                                        </Grid>

                                        <Grid item xs={3}>
                                            <SoftBox ml={0.5} mb={0.25}>
                                                <FormSwitch label="SNG" readOnly={!canEditSchedule()}
                                                    name={excludeRevenueFromTax.name}
                                                    value={excludeRevenueFromTaxV}
                                                    mask={2}
                                                    variant="bit"
                                                    {...formData} />
                                            </SoftBox>
                                        </Grid>

                                        <Grid item xs={3}>
                                            <SoftBox ml={0.5} mb={0.25}>
                                                <FormSwitch label="MTT" readOnly={!canEditSchedule()}
                                                    name={excludeRevenueFromTax.name}
                                                    value={excludeRevenueFromTaxV}
                                                    mask={4}
                                                    variant="bit"
                                                    {...formData} />
                                            </SoftBox>
                                        </Grid>

                                        <Grid item xs={3}>
                                            <SoftBox ml={0.5} mb={0.25}>
                                                <FormSwitch label="OFC" readOnly={!canEditSchedule()}
                                                    name={excludeRevenueFromTax.name}
                                                    value={excludeRevenueFromTaxV}
                                                    mask={8}
                                                    variant="bit"
                                                    {...formData} />
                                            </SoftBox>
                                        </Grid>

                                        <Grid item xs={3}>
                                            <SoftBox ml={0.5} mb={0.25}>
                                                <FormSwitch label="SPIN" readOnly={!canEditSchedule()}
                                                    name={excludeRevenueFromTax.name}
                                                    value={excludeRevenueFromTaxV}
                                                    mask={16}
                                                    variant="bit"
                                                    {...formData} />
                                            </SoftBox>
                                        </Grid>

                                        <Grid item xs={3}>
                                            <SoftBox ml={0.5} mb={0.25}>
                                                <FormSwitch label="Leaderboard" readOnly={!canEditSchedule()}
                                                    name={excludeRevenueFromTax.name}
                                                    value={excludeRevenueFromTaxV}
                                                    mask={64}
                                                    variant="bit"
                                                    {...formData} />
                                            </SoftBox>
                                        </Grid>

                                        <Grid item xs={3}>
                                            <SoftBox ml={0.5} mb={0.25}>
                                                <FormSwitch label="BBJ" readOnly={!canEditSchedule()}
                                                    name={excludeRevenueFromTax.name}
                                                    value={excludeRevenueFromTaxV}
                                                    mask={32}
                                                    variant="bit"
                                                    {...formData} />
                                            </SoftBox>
                                        </Grid>



                                    </Grid> : null}
                            </Grid>
                        </Grid>
                            : null}

                    </SoftBox>
                </Grid>
            </Grid >
        </SoftBox >
    </SoftBox >;
};

FeeScheduleEditorForm.propTypes = {
    cardoom: PropTypes.object.isRequired,
    club: PropTypes.object.isRequired,
    dense: PropTypes.bool
};

const FeeScheduleEditor = ({ cardroom, club, book, schedule, onClose, dense }) => {

    const { formId, formField } = form;
    const currentValidation = (cardroom.type === 6 && book) ? validations.book : validations.club;

    const [retryCounter, setRetryCounter] = useState(0);
    const [scheduleRecord, setScheduleRecord] = useState(null);

    function handleRetry() {
        setRetryCounter(retryCounter + 1);
    }
    const [showProgress, progressIndicatorProps] = useProgressIndicator("wait", "saving commission schedule...", handleRetry)

    const { error, execute } = useFetchWithMsal({
        scopes: cardroom.type === 6 ? apiConfig.book.scopes.write : apiConfig.club.scopes.write,
    });

    const [context, actions, features] = useCardroomContext(cardroom);

    function canEditSchedule() {
        if (!context) return false;

        return context.isAllowed(actions.cardroom.fees.agent.edit);
    }

    const handleSubmit = (values, actions) => {

        //alert("submitting");

        if (false === canEditSchedule()) return;

        //construct participant object
        const ns = Object.assign({}, schedule);

        //update schedule...
        if (!ns.id) ns.id = "00000000-0000-0000-0000-000000000000";

        ns.name = values.name;
        ns.description = values.description;
        ns.rate = parseFloat(values.feeRate) / 100;
        ns.scheme = values.feeScheduleScheme;
        ns.threshold = values.feeThreshold;

        if (cardroom.type !== 6) {
            ns.aggregate = values.aggregate;
            ns.chipOffsetRate = parseFloat(values.chipOffsetRate) / 100;
            ns.taxRebateRate = parseFloat(values.taxRebateRate) / 100;

            ns.excludeFromTax = parseInt(values.excludeRevenueFromTax);

        } else {
            ns.makeupThreshold = values.makeupThreshold;            
        }

        setScheduleRecord(ns);
    };


    useEffect(() => {
        if (scheduleRecord) {

            progressIndicatorProps.setMode("wait");
            progressIndicatorProps.setMessage("Saving fee schedule...")
            showProgress();


            let url = "";

            if (cardroom.type === 6) {
                url = apiConfig.book.endpoint + "/sportsbook/" + book.id + "/casino/" + cardroom.id + "/schedules/commission";
            }
            else {
                url = apiConfig.club.endpoint + "/club/" + club.id + "/casino/" + cardroom.id + "/schedules/commission"
            }

            execute("POST", url, scheduleRecord).then((response) => {
                if (response) {
                    //raiseOnTransactionCreated();
                    if (schedule) schedule = Object.assign(schedule, response);
                    else schedule = response;
                }

                setScheduleRecord(null);
                progressIndicatorProps.close();
                raiseOnClose(false);

            }).catch((e) => {
                progressIndicatorProps.setMode("errorWithRetry");
                progressIndicatorProps.setMessage("Unable to save fee schedule, please try again...")
            });
        }

    }, [scheduleRecord, execute, retryCounter]);


    function onCanceled() {
        raiseOnClose(true);
    }

    function raiseOnClose(canceled) {
        if (onClose) {
            onClose(canceled);
        }
    }

    return <Formik
        initialValues={initialValues}
        validationSchema={currentValidation}
        onSubmit={handleSubmit}
    >
        {({ values, errors, touched, setFieldValue, setFieldError, setFieldTouched, validateForm, resetForm, isSubmitting }) => {
            return <Form id={formId} autoComplete="off">

                <SoftBox p={2} >
                    <SoftBox >
                        <ProgressIndicator {...progressIndicatorProps} />

                        {progressIndicatorProps.visible ? <></> : <>
                            <FeeScheduleEditorForm cardroom={cardroom} club={club} book={book} schedule={schedule} dense={dense} formData={{
                                values,
                                touched,
                                formField,
                                errors,
                                setFieldValue,
                                setFieldError,
                                setFieldTouched,
                                validateForm,
                                resetForm
                            }} />

                            <SoftBox mt={3} width="100%" display="flex" justifyContent="space-between">

                                <SoftButton variant="gradient" color="light" onClick={onCanceled}>
                                    cancel
                                </SoftButton>

                                <SoftButton
                                    variant="gradient"
                                    color="dark"
                                    disabled={isSubmitting || !canEditSchedule()}
                                    type="submit"
                                >
                                    save
                                </SoftButton>
                            </SoftBox>
                        </>

                        }

                    </SoftBox>
                </SoftBox>

            </Form>
        }}
    </Formik>;
};

FeeScheduleEditor.propTypes = {
    cardoom: PropTypes.object.isRequired,
    club: PropTypes.object.isRequired,
    dense: PropTypes.bool
};

export default FeeScheduleEditor;